<script setup lang="ts">
  import { useMultiStepStore } from '@/components/forms/multi-step/store'

  const multiStepStore = useMultiStepStore()

  async function proceedToNextStep(setValuesCallback: () => void, isValid: boolean) {
    if (setValuesCallback instanceof Function) {
      setValuesCallback()
    }

    if (!isValid) {
      getFirstElementWithError()
      multiStepStore.triggerSubmit = false
      multiStepStore.showLoading = false

      return
    }

    scrollToTop()

    if (!multiStepStore.isLastStep) {
      multiStepStore.nextStep()
    }

    multiStepStore.showLoading = false
  }

  function prevStep() {
    if (multiStepStore.currentStep === 1) {
      multiStepStore.showMultiStepForm = false
      return
    }

    multiStepStore.showLoading = true
    multiStepStore.prevStep()
    multiStepStore.triggerSubmit = false
    scrollToTop()
    multiStepStore.showLoading = false
  }

  function nextStep() {
    multiStepStore.showLoading = true
    multiStepStore.triggerSubmit = true
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function getFirstElementWithError() {
    const sectionError = document.querySelector('.section-error') as HTMLElement | null
    if (sectionError) {
      const headerHeight = document.querySelector('header')?.clientHeight ?? 0
      window.scrollTo({ top: sectionError.offsetTop - headerHeight, behavior: 'smooth' })
    }
  }
</script>
<template>
  <div>
    <div
      v-if="multiStepStore.showLoading"
      class="fixed left-0 top-0 z-50 flex h-screen w-full items-center justify-center bg-black/25"
    >
      <UiPlaneLoader />
    </div>
    <LazyFormsMultiStepProgress />
    <div class="grid min-h-[400px] grid-cols-1 md:grid-cols-[744px_auto] md:gap-6 xl:grid-cols-[1044px_auto]">
      <div class="flex flex-col justify-between rounded-sm px-4 py-6 shadow-lg md:px-10 md:py-8">
        <div>
          <Transition
            name="fade"
            mode="out-in"
            @after-enter="multiStepStore.triggerSubmit = false"
          >
            <slot
              name="forms"
              :proceed-to-next-step="proceedToNextStep"
            />
          </Transition>
        </div>
        <div class="mt-6 flex flex-col justify-between gap-4 text-subtitle-1 md:mt-10 md:flex-row">
          <UiButton
            :title="$t('forms.back')"
            icon="common/arrow-back"
            variant="outline"
            size="lg"
            class="order-2 w-full justify-center md:order-1 md:w-fit"
            :disabled="multiStepStore.triggerSubmit"
            @click.prevent="prevStep()"
          />
          <UiButton
            :title="multiStepStore.isLastStep ? $t('forms.askQuestions.submitRequest') : $t('forms.next')"
            variant="secondary"
            size="lg"
            class="order-1 w-full md:order-2 md:w-fit"
            :class="{ '!bg-bt-green !text-bt-blue-ui': multiStepStore.isLastStep }"
            :disabled="multiStepStore.triggerSubmit"
            @click.prevent="nextStep()"
          />
        </div>
      </div>
      <div
        v-if="multiStepStore.currentStep > 1"
        class="hidden flex-col gap-6 md:flex"
      >
        <LazyFormsMultiStepRecordsPreview
          v-for="field in multiStepStore.dataFieldsPreview"
          :key="field.title"
          :data="field.data"
          :title="field.title"
          :step="field.step"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
